/**
 * Sélecteur de période, repris du menu de la barre latérale.
 *
 * Mêmes périodes, mêmes libellés, même mécanique : on choisit dans une liste ou on saisit
 * deux dates, et rien n'est appliqué avant « Appliquer ». Le brouillon vit dans le
 * composant, l'état retenu chez l'appelant — ce qui permet d'annuler sans avoir rien
 * changé au tableau derrière.
 *
 * **Avec comparaison, désormais.** Ce bloc n'existait pas ici : le comparatif du consolidé
 * déduisait sa référence de la période choisie, et l'an dernier était la seule réponse
 * possible. Il porte maintenant le même « Comparer » que le menu de la barre latérale —
 * mêmes libellés, même toggle, même calendrier bicolore — parce que la référence est un
 * réglage de période, pas un réglage de tableau : la poser ailleurs obligeait à la chercher
 * à deux endroits.
 *
 * La barre latérale n'étant pas montée sur le consolidé (`App.js`, la vue s'affiche pleine
 * largeur), ce composant est le seul endroit où ce réglage peut vivre pour cet écran.
 *
 * **Deux bases de plus que le menu de la barre latérale**, parce que ce tableau s'y prête :
 * « Période précédente » et, surtout, « Opération commerciale » — comparer deux opérations
 * sans avoir à retrouver leurs dates est précisément ce qu'on vient y faire.
 *
 * Les dates de référence ne sont pas calculées ici : `bornesDeComparaison` en décide, et le
 * tableau l'appelle sur les bornes que le serveur lui rend. Ce panneau n'en montre qu'un
 * aperçu, tiré de la même fonction — deux calculs concurrents finiraient par se contredire.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'lucide-react';
import { getDateRangeFromPeriod } from '../utils/periodUtils';
import { BASES_COMPARAISON, BASES_A_DATES_EXPLICITES, bornesDeComparaison } from '../utils/comparaisonPeriode';

/** Périodes proposées, dans l'ordre et avec les libellés de la barre latérale. */
export const PERIODES = [
  { value: 'CUSTOM', label: 'Personnalisée' },
  { value: 'TODAY', label: "Aujourd'hui" },
  { value: 'YESTERDAY', label: 'Hier' },
  { value: 'LAST_7_DAYS', label: '7 derniers jours' },
  { value: 'LAST_14_DAYS', label: '14 derniers jours' },
  { value: 'LAST_30_DAYS', label: '30 derniers jours' },
  { value: 'THIS_MONTH', label: 'Ce mois' },
  { value: 'LAST_MONTH', label: 'Mois dernier' },
  { value: 'LAST_90_DAYS', label: '90 derniers jours' },
  { value: 'THIS_YEAR', label: 'Année en cours' },
  { value: 'LAST_YEAR', label: 'Année dernière' }
];

/** Une date ISO en `JJ/MM/AAAA`. Exportée : le calendrier des notes en a besoin, et le
    projet compte déjà une trentaine de formateurs de dates recopiés. */
export const enFrancais = (iso) => {
  if (!iso) return '';
  const [a, m, j] = iso.split('-');
  return `${j}/${m}/${a}`;
};

const STYLE_INTERTITRE = {
  fontSize: '11px', fontWeight: 600, color: '#9ca3af',
  textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px', lineHeight: 1.4
};

const STYLE_SELECT_OPERATION = {
  width: '100%', padding: '6px 8px', borderRadius: '6px',
  border: '1px solid #e5e7eb', fontSize: '12px', color: '#374151',
  backgroundColor: 'white', cursor: 'pointer'
};

/**
 * Le nom d'une opération, suivi de ses dates.
 *
 * Deux opérations portent souvent le même nom d'une année sur l'autre — « Soldes », « Black
 * Friday ». Sans son année, la liste en proposerait deux identiques et le choix serait un
 * pari ; c'est précisément ce qu'on vient éviter en ne cherchant plus les dates à la main.
 */
const NOM_OPERATION_MAX = 28;

const libelleOperation = (op) => {
  const annee = (op.debut || '').slice(0, 4);
  const jour = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  // Tronqué comme les colonnes de la vue Opérations le sont côté serveur : le contenu d'une
  // note n'a pas de longueur maximale, et une seule phrase étirerait le menu hors du panneau.
  const nom = op.nom.length > NOM_OPERATION_MAX ? `${op.nom.slice(0, NOM_OPERATION_MAX - 1)}…` : op.nom;
  if (!op.debut) return nom;
  const plage = op.fin && op.fin !== op.debut ? `${jour(op.debut)}→${jour(op.fin)}` : jour(op.debut);
  return `${nom} · ${plage}/${annee.slice(2)}`;
};

/**
 * Les dates d'une période, qu'elle soit nommée ou saisie.
 *
 * `getDateRangeFromPeriod` ne sait pas traiter `CUSTOM` — elle y retombe sur trente jours
 * glissants —, d'où ce court-circuit, que trois endroits du composant réclamaient.
 */
const datesDeLaPeriode = (period, debut, fin) => (
  period === 'CUSTOM' && debut && fin
    ? { startDate: debut, endDate: fin }
    : getDateRangeFromPeriod(period || 'LAST_7_DAYS')
);

/**
 * Le libellé de la référence, dans le champ fermé — « vs 13/09/2026 - 19/09/2026 ».
 *
 * Mot pour mot celui de la barre latérale, une date seule quand les deux bornes coïncident
 * comprise : c'est le même réglage, et deux formulations obligeraient à traduire de l'un à
 * l'autre. `null` quand il n'y a rien à dire, pour que l'appelant n'affiche pas de ligne vide.
 */
export const libelleDeLaComparaison = (debut, fin) => {
  if (!debut || !fin) return null;
  const jour = (iso) => {
    const [a, m, j] = iso.split('-');
    return `${j}/${m}/${a}`;
  };
  return debut === fin ? `vs ${jour(debut)}` : `vs ${jour(debut)} - ${jour(fin)}`;
};

/** Libellé affiché dans le champ fermé. */
export const libelleDeLaPeriode = (period, debut, fin) => {
  if (period === 'CUSTOM' && debut && fin) return `${enFrancais(debut)} - ${enFrancais(fin)}`;
  return PERIODES.find(p => p.value === period)?.label || '7 derniers jours';
};

/**
 * @param {object} props
 * @param {object} [props.comparaison] `{ active, base, debut, fin, operationId }` — l'état
 *   retenu chez l'appelant, sur le modèle de la période : le panneau n'en fait qu'un brouillon.
 * @param {object[]} [props.operations] `[{ id, nom, debut, fin }]` — les opérations commerciales
 *   proposées comme période ou comme référence. Chargées par l'appelant, qui seul connaît le
 *   périmètre : ce composant ne sait pas à quel client il appartient.
 * @param {function} props.onChange `(periode, debut, fin, comparaison)`
 */
const PeriodSelector = ({
  selectedPeriod,
  customStartDate,
  customEndDate,
  onChange,
  comparaison = null,
  operations = []
}) => {
  const [ouvert, setOuvert] = useState(false);
  const [brouillonPeriode, setBrouillonPeriode] = useState(selectedPeriod || 'LAST_7_DAYS');
  const [brouillonDebut, setBrouillonDebut] = useState(customStartDate || '');
  const [brouillonFin, setBrouillonFin] = useState(customEndDate || '');
  /**
   * Champ que le prochain clic dans le calendrier renseignera.
   *
   * `null` = début d'une nouvelle plage : le premier clic pose le début, le second la fin.
   * `compDebut` / `compFin` visent la plage de référence — c'est ce qui permet de la dessiner
   * dans le même calendrier que la période, en orange, sans second panneau.
   */
  const [champActif, setChampActif] = useState(null);

  /**
   * Le brouillon de comparaison. `base` vaut par défaut **la période précédente**.
   *
   * Le comparatif comparait à l'an dernier, faute de pouvoir faire autrement. Une fois le
   * réglage ouvert, c'est la période précédente qu'on attend d'un tableau qu'on vient
   * d'ouvrir : sept jours regardés contre les sept d'avant. L'année précédente reste à un
   * clic, et garde alors la durée de la période.
   */
  const [brouillonComparer, setBrouillonComparer] = useState(true);
  const [brouillonBase, setBrouillonBase] = useState('previous_period');
  const [brouillonCompDebut, setBrouillonCompDebut] = useState('');
  const [brouillonCompFin, setBrouillonCompFin] = useState('');
  /**
   * L'opération retenue comme **référence**. Seul son nom sert à l'affichage : ce sont ses
   * bornes qui font foi, et elles sont recopiées dans les brouillons de dates.
   *
   * Il n'y a pas d'équivalent pour la période elle-même : la barre du comparatif la désigne,
   * et un second chemin ici aurait laissé deux réglages dire la même chose.
   */
  const [operationComparaison, setOperationComparaison] = useState('');

  const conteneur = useRef(null);

  // Rouvrir le menu repart de l'état retenu, jamais d'un brouillon abandonné
  useEffect(() => {
    if (!ouvert) return;
    setBrouillonPeriode(selectedPeriod || 'LAST_7_DAYS');
    const dates = datesDeLaPeriode(selectedPeriod, customStartDate, customEndDate);
    setBrouillonDebut(dates.startDate);
    setBrouillonFin(dates.endDate);
    setChampActif(null);

    setBrouillonComparer(comparaison ? comparaison.active !== false : true);
    const base = comparaison?.base || 'previous_period';
    setBrouillonBase(base);
    setOperationComparaison(comparaison?.operationId || '');

    // Les bases calculées n'ont pas de dates retenues : on en recalcule l'aperçu, pour que le
    // calendrier montre la référence dès l'ouverture plutôt qu'après le premier clic.
    const calculees = bornesDeComparaison(dates.startDate, dates.endDate, base);
    setBrouillonCompDebut(calculees ? calculees.debut : (comparaison?.debut || ''));
    setBrouillonCompFin(calculees ? calculees.fin : (comparaison?.fin || ''));
  }, [ouvert, selectedPeriod, customStartDate, customEndDate, comparaison]);

  // Fermeture au clic extérieur, comme le menu de la barre latérale
  useEffect(() => {
    if (!ouvert) return;
    const auClic = (e) => {
      if (conteneur.current && !conteneur.current.contains(e.target)) setOuvert(false);
    };
    document.addEventListener('mousedown', auClic);
    return () => document.removeEventListener('mousedown', auClic);
  }, [ouvert]);

  /**
   * L'aperçu de la référence suit la période, sans que chaque interaction ait à y penser.
   *
   * Un effet plutôt qu'un appel après chaque geste : la plage se pose en deux clics dans le
   * calendrier, et rafraîchir au premier montrait une référence calculée sur une plage
   * incomplète. Ici, le recalcul n'a lieu que quand les deux bornes sont là.
   *
   * Les bases à dates explicites — « Personnalisée », « Opération » — rendent `null` et ne
   * sont donc jamais écrasées : leurs dates viennent de l'utilisateur, pas d'un calcul.
   */
  useEffect(() => {
    if (!ouvert) return;
    const calculees = bornesDeComparaison(brouillonDebut, brouillonFin, brouillonBase);
    if (!calculees) return;
    setBrouillonCompDebut(calculees.debut);
    setBrouillonCompFin(calculees.fin);
  }, [ouvert, brouillonDebut, brouillonFin, brouillonBase]);

  /**
   * Changer de période libère une référence figée sur des dates.
   *
   * Une base « Opération » ou « Personnalisée » porte des bornes qui ne se recalculent pas :
   * elles désignaient un point de comparaison choisi pour la période d'alors. Passer à
   * « 7 derniers jours » sans les libérer gardait le tableau braqué sur l'opération d'il y a
   * six mois — un rapprochement que plus rien ne justifiait, et que rien n'annonçait.
   *
   * Elle retombe donc sur la période précédente, qui suit la nouvelle période. Choisir
   * délibérément une opération ou des dates **après** avoir posé sa période reste possible :
   * c'est le geste suivant, et il prime.
   */
  const libererReferenceFigee = () => {
    if (!BASES_A_DATES_EXPLICITES.includes(brouillonBase)) return;
    setBrouillonBase('previous_period');
    setOperationComparaison('');
  };

  const choisir = (value) => {
    setBrouillonPeriode(value);
    libererReferenceFigee();
    // « Personnalisée » garde les dates affichées : elles servent de point de départ à la
    // saisie. Les autres périodes remplissent les champs, pour qu'on voie ce qu'on choisit.
    if (value !== 'CUSTOM') {
      const { startDate, endDate } = getDateRangeFromPeriod(value);
      setBrouillonDebut(startDate);
      setBrouillonFin(endDate);
    }
  };

  /** Une opération choisie comme référence pose ses bornes du côté de la comparaison. */
  const choisirOperation = (operation) => {
    setOperationComparaison(operation.id);
    setBrouillonBase('operation');
    setBrouillonComparer(true);
    setBrouillonCompDebut(operation.debut);
    setBrouillonCompFin(operation.fin);
  };

  /**
   * Un clic dans le calendrier pose une borne.
   *
   * Trois cas, comme dans la barre latérale : le champ « début » explicitement visé, le
   * champ « fin » explicitement visé, ou aucun — et le calendrier enchaîne alors début puis
   * fin. Une fin antérieure au début inverse les deux plutôt que de refuser le clic : c'est
   * ce que la personne voulait dire, et un refus silencieux passerait pour un bug.
   */
  const choisirJour = (jour) => {
    /**
     * Un clic destiné à la référence ne touche pas à la période.
     *
     * Il bascule la base sur « Personnalisée » : désigner une date de référence à la main,
     * c'est précisément cela, et laisser la base sur « Année précédente » ferait aussitôt
     * écraser le clic par le recalcul.
     */
    if (champActif === 'compDebut' || champActif === 'compFin') {
      setBrouillonBase('custom');
      setBrouillonComparer(true);
      setOperationComparaison('');
      if (champActif === 'compDebut') {
        setBrouillonCompDebut(jour);
        if (!brouillonCompFin || brouillonCompFin < jour) setBrouillonCompFin(jour);
        setChampActif('compFin');
      } else {
        if (brouillonCompDebut && jour < brouillonCompDebut) {
          setBrouillonCompFin(brouillonCompDebut);
          setBrouillonCompDebut(jour);
        } else {
          setBrouillonCompFin(jour);
        }
        setChampActif(null);
      }
      return;
    }

    setBrouillonPeriode('CUSTOM');
    libererReferenceFigee();

    if (champActif === 'fin') {
      if (brouillonDebut && jour < brouillonDebut) {
        setBrouillonFin(brouillonDebut);
        setBrouillonDebut(jour);
      } else {
        setBrouillonFin(jour);
      }
      setChampActif(null);
      return;
    }

    if (champActif === 'debut') {
      setBrouillonDebut(jour);
      if (!brouillonFin || brouillonFin < jour) setBrouillonFin(jour);
      setChampActif('fin');
      return;
    }

    // Aucun champ visé : on repart d'une plage neuve
    setBrouillonDebut(jour);
    setBrouillonFin('');
    setChampActif('fin');
  };

  /**
   * Ce que le champ fermé annonce comme référence.
   *
   * Calculé sur la période **retenue** et non sur le brouillon : le champ fermé montre l'état
   * du tableau derrière, pas celui d'un panneau qu'on vient d'abandonner.
   *
   * Les bases à dates choisies portent les leurs ; les autres se déduisent ici de la même
   * fonction que le tableau, ce qui garantit que l'étiquette dit bien la période qui a été
   * demandée. `null` quand rien n'est comparé — la ligne disparaît alors.
   */
  const libelleReference = (() => {
    if (!comparaison || comparaison.active === false) return null;
    const dates = datesDeLaPeriode(selectedPeriod, customStartDate, customEndDate);
    const bornes = BASES_A_DATES_EXPLICITES.includes(comparaison.base)
      ? (comparaison.debut && comparaison.fin ? { debut: comparaison.debut, fin: comparaison.fin } : null)
      : bornesDeComparaison(dates.startDate, dates.endDate, comparaison.base);
    return bornes ? libelleDeLaComparaison(bornes.debut, bornes.fin) : null;
  })();

  /**
   * Y a-t-il quelque chose à appliquer ?
   *
   * « Appliquer » n'est mis en avant — fond plein — que si le brouillon diffère de la
   * période retenue. Au repos c'est un simple lien : rien à valider, rien à souligner.
   */
  const aDesChangements = (() => {
    if (brouillonPeriode !== (selectedPeriod || 'LAST_7_DAYS')) return true;
    if (brouillonPeriode === 'CUSTOM'
      && (brouillonDebut !== customStartDate || brouillonFin !== customEndDate)) return true;

    const retenue = comparaison || {};
    if (brouillonComparer !== (retenue.active !== false)) return true;
    if (brouillonBase !== (retenue.base || 'previous_period')) return true;
    // Seules les bases à dates choisies comparent leurs bornes : celles des autres sont
    // déduites, et un aperçu recalculé ne constitue pas un changement à appliquer.
    if (BASES_A_DATES_EXPLICITES.includes(brouillonBase)) {
      return brouillonCompDebut !== (retenue.debut || '') || brouillonCompFin !== (retenue.fin || '');
    }
    return false;
  })();

  /**
   * Ce que « Appliquer » remonte.
   *
   * La comparaison part **toujours**, même éteinte : l'appelant doit pouvoir distinguer
   * « on ne compare pas » d'un réglage absent, sans quoi éteindre le toggle n'aurait aucun effet.
   *
   * Les bornes ne sont transmises que pour les bases qui en portent. Pour les autres, c'est le
   * tableau qui les dérive des dates que le serveur lui rend — lui seul sait où la période
   * nommée s'est réellement arrêtée.
   */
  const appliquer = () => {
    const explicite = BASES_A_DATES_EXPLICITES.includes(brouillonBase);
    if (explicite && brouillonComparer && (!brouillonCompDebut || !brouillonCompFin)) return;

    const referenceChoisie = {
      active: brouillonComparer,
      base: brouillonBase,
      debut: explicite ? brouillonCompDebut : '',
      fin: explicite ? brouillonCompFin : '',
      operationId: brouillonBase === 'operation' ? operationComparaison : ''
    };

    if (brouillonPeriode === 'CUSTOM') {
      if (!brouillonDebut || !brouillonFin) return;
      onChange('CUSTOM', brouillonDebut, brouillonFin, referenceChoisie);
    } else {
      onChange(brouillonPeriode, undefined, undefined, referenceChoisie);
    }
    setOuvert(false);
  };

  return (
    <div ref={conteneur} style={{ position: 'relative' }}>
      <button
        onClick={() => setOuvert(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb',
          backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem', color: '#1f2937'
        }}
      >
        <Calendar size={15} />
        {/* Période et référence empilées, comme dans le menu de la barre latérale : la seconde
            ligne dit à quoi on compare sans qu'il faille rouvrir le panneau pour le savoir. */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
          <span>{libelleDeLaPeriode(selectedPeriod, customStartDate, customEndDate)}</span>
          {libelleReference && (
            <span style={{ fontSize: '0.75rem', color: '#f97316', fontWeight: 500 }}>
              {libelleReference}
            </span>
          )}
        </div>
        <span style={{ color: '#9ca3af' }}>⌄</span>
      </button>

      {ouvert && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 1000,
          backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: '12px', minWidth: '560px'
        }}>
          <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: '0 0 200px', borderRight: '1px solid #e5e7eb', paddingRight: '16px' }}>
            {PERIODES.map(periode => (
              <button
                key={periode.value}
                onClick={() => choisir(periode.value)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 12px', border: 'none', borderRadius: '8px',
                  backgroundColor: brouillonPeriode === periode.value ? 'rgba(102, 126, 234, 0.12)' : 'transparent',
                  color: brouillonPeriode === periode.value ? '#4c51bf' : '#374151',
                  fontWeight: brouillonPeriode === periode.value ? 600 : 400,
                  fontSize: '0.85rem', cursor: 'pointer'
                }}
              >
                {periode.label}
              </button>
            ))}


            {/* Le bloc « Comparer », repris du menu de la barre latérale : mêmes libellés, même
                toggle, même position sous la liste des périodes. */}
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>Comparer</span>
                <label className="toggle-switch" style={{ transform: 'scale(0.8)' }}>
                  <input
                    type="checkbox"
                    checked={brouillonComparer}
                    onChange={(e) => setBrouillonComparer(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {brouillonComparer && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {BASES_COMPARAISON.map(option => (
                    <label
                      key={option.value}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '6px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
                        color: brouillonBase === option.value ? '#c2410c' : '#4b5563',
                        background: brouillonBase === option.value ? 'rgba(249, 115, 22, 0.1)' : 'transparent'
                      }}
                    >
                      <input
                        type="radio"
                        name="baseComparaison"
                        checked={brouillonBase === option.value}
                        onChange={() => {
                          setBrouillonBase(option.value);
                          if (option.value !== 'operation') setOperationComparaison('');
                          // Viser d'emblée la première borne : « Personnalisée » choisie, le
                          // clic suivant dans le calendrier doit servir la référence, pas la
                          // période — sans quoi on croit le réglage inopérant.
                          setChampActif(option.value === 'custom' ? 'compDebut' : null);
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}

                  {/* La liste des opérations n'apparaît que sous sa propre base : la montrer
                      en permanence laisserait croire qu'elle s'applique aux autres. */}
                  {brouillonBase === 'operation' && (
                    operations.length > 0 ? (
                      <select
                        value={operationComparaison}
                        onChange={(e) => {
                          const op = operations.find(o => o.id === e.target.value);
                          if (op) choisirOperation(op);
                        }}
                        style={{ ...STYLE_SELECT_OPERATION, marginTop: '4px' }}
                      >
                        <option value="">Choisir…</option>
                        {operations.map(op => (
                          <option key={op.id} value={op.id}>{libelleOperation(op)}</option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ ...STYLE_INTERTITRE, marginTop: '4px', textTransform: 'none', letterSpacing: 0 }}>
                        Aucune opération commerciale n'est enregistrée. Elles se créent dans les
                        notes, en choisissant ce sujet.
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              {[
                { cle: 'debut', libelle: 'Date de début', valeur: brouillonDebut, poser: setBrouillonDebut, max: brouillonFin },
                { cle: 'fin', libelle: 'Date de fin', valeur: brouillonFin, poser: setBrouillonFin, min: brouillonDebut }
              ].map((champ, index) => (
                <React.Fragment key={champ.cle}>
                  {index === 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', paddingTop: '18px', color: '#9ca3af' }}>-</div>
                  )}
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '11px', color: '#667eea', display: 'block', marginBottom: '4px', fontWeight: 500, textAlign: 'center' }}>
                      {champ.libelle}
                    </label>
                    <input
                      type="date"
                      value={champ.valeur || ''}
                      max={champ.max || undefined}
                      min={champ.min || undefined}
                      onFocus={() => setChampActif(champ.cle)}
                      onChange={(e) => {
                        champ.poser(e.target.value);
                        // Toucher une date, c'est définir une période personnalisée
                        setBrouillonPeriode('CUSTOM');
                                            libererReferenceFigee();
                      }}
                      style={{
                        width: '100%', padding: '8px', borderRadius: '6px',
                        border: champActif === champ.cle ? '2px solid #667eea' : '1px solid #667eea',
                        fontSize: '13px', boxSizing: 'border-box', outline: 'none', textAlign: 'center'
                      }}
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Les bornes de la référence, sous celles de la période et dans sa couleur.
                Affichées même quand elles sont déduites : c'est là qu'on lit à quoi « année
                précédente » correspond réellement, ce qu'un libellé ne dit pas. */}
            {brouillonComparer && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                {[
                  { cle: 'compDebut', libelle: 'Comparé du', valeur: brouillonCompDebut, poser: setBrouillonCompDebut },
                  { cle: 'compFin', libelle: 'au', valeur: brouillonCompFin, poser: setBrouillonCompFin }
                ].map((champ, index) => (
                  <React.Fragment key={champ.cle}>
                    {index === 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', paddingTop: '18px', color: '#9ca3af' }}>-</div>
                    )}
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '11px', color: '#f97316', display: 'block', marginBottom: '4px', fontWeight: 500, textAlign: 'center' }}>
                        {champ.libelle}
                      </label>
                      <input
                        type="date"
                        value={champ.valeur || ''}
                        onFocus={() => setChampActif(champ.cle)}
                        onChange={(e) => {
                          champ.poser(e.target.value);
                          // Saisir une date de référence, c'est la désigner soi-même.
                          setBrouillonBase('custom');
                          setOperationComparaison('');
                        }}
                        style={{
                          width: '100%', padding: '8px', borderRadius: '6px',
                          border: champActif === champ.cle ? '2px solid #f97316' : '1px solid #f97316',
                          fontSize: '13px', boxSizing: 'border-box', outline: 'none', textAlign: 'center'
                        }}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Calendrier défilant sur trois ans, ouvert sur le mois courant — la plage
                choisie s'y lit d'un coup d'œil, ce qu'aucun champ de saisie ne montre. */}
            <div
              ref={(el) => { if (el && el.dataset.place !== 'oui') { el.scrollTop = el.scrollHeight; el.dataset.place = 'oui'; } }}
              style={{ maxHeight: '260px', overflowY: 'auto', paddingRight: '8px', marginTop: '12px' }}
            >
              {(() => {
                const aujourdhui = new Date();
                const NOMS_MOIS = ['JANV.', 'FÉVR.', 'MARS', 'AVR.', 'MAI', 'JUIN', 'JUIL.', 'AOÛT', 'SEPT.', 'OCT.', 'NOV.', 'DÉC.'];
                const NOMS_JOURS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

                const mois = [];
                for (let decalage = -35; decalage <= 0; decalage++) {
                  const d = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() + decalage, 1);
                  mois.push({ mois: d.getMonth(), annee: d.getFullYear() });
                }

                return mois.map(({ mois: m, annee }) => {
                  const premierJour = new Date(annee, m, 1).getDay();
                  // Semaine commençant le lundi : dimanche (0) passe en fin de semaine
                  const decalageInitial = premierJour === 0 ? 6 : premierJour - 1;
                  const nbJours = new Date(annee, m + 1, 0).getDate();

                  const cases = [];
                  for (let i = 0; i < decalageInitial; i++) {
                    cases.push(<div key={`vide-${annee}-${m}-${i}`} style={{ width: '24px', height: '24px' }} />);
                  }

                  for (let jour = 1; jour <= nbJours; jour++) {
                    const iso = `${annee}-${String(m + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
                    const estAujourdhui = jour === aujourdhui.getDate() && m === aujourdhui.getMonth() && annee === aujourdhui.getFullYear();
                    const estFutur = new Date(annee, m, jour) > aujourdhui;
                    const estBorne = iso === brouillonDebut || iso === brouillonFin;
                    const dansLaPlage = brouillonDebut && brouillonFin && iso >= brouillonDebut && iso <= brouillonFin;
                    const estBorneComp = brouillonComparer && (iso === brouillonCompDebut || iso === brouillonCompFin);
                    const dansLaComparaison = brouillonComparer && brouillonCompDebut && brouillonCompFin
                      && iso >= brouillonCompDebut && iso <= brouillonCompFin;

                    /**
                     * Deux plages dans un seul calendrier.
                     *
                     * La période garde le bleu, la référence prend l'orange — les couleurs du
                     * menu de la barre latérale. Un jour qui appartient aux deux existe : une
                     * référence peut chevaucher la période quand on la désigne à la main. Il
                     * porte alors les deux teintes en dégradé, plutôt que d'en cacher une.
                     */
                    const fond = (() => {
                      if (estBorne && estBorneComp) return 'linear-gradient(90deg, #667eea 50%, #f97316 50%)';
                      if (estBorne) return '#667eea';
                      if (estBorneComp) return '#f97316';
                      if (dansLaPlage && dansLaComparaison) {
                        return 'linear-gradient(90deg, rgba(102, 126, 234, 0.2) 50%, rgba(249, 115, 22, 0.2) 50%)';
                      }
                      if (dansLaPlage) return 'rgba(102, 126, 234, 0.15)';
                      if (dansLaComparaison) return 'rgba(249, 115, 22, 0.15)';
                      return 'transparent';
                    })();

                    cases.push(
                      <button
                        key={`jour-${iso}`}
                        disabled={estFutur}
                        onClick={() => choisirJour(iso)}
                        style={{
                          width: '24px', height: '24px', border: 'none', padding: 0,
                          fontSize: '11px', cursor: estFutur ? 'default' : 'pointer',
                          background: fond,
                          color: estFutur ? '#d1d5db'
                            : (estBorne || estBorneComp ? 'white' : (estAujourdhui ? '#667eea' : '#374151')),
                          fontWeight: estAujourdhui || estBorne || estBorneComp ? 600 : 400,
                          borderRadius: estBorne || estBorneComp ? '50%' : '4px'
                        }}
                      >
                        {jour}
                      </button>
                    );
                  }

                  return (
                    <div key={`${annee}-${m}`} style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: 600, fontSize: '11px', marginBottom: '6px', color: '#6b7280', letterSpacing: '0.5px' }}>
                        {NOMS_MOIS[m]} {annee}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', marginBottom: '2px' }}>
                        {NOMS_JOURS.map((n, i) => (
                          <div key={i} style={{ width: '24px', textAlign: 'center', fontSize: '9px', color: '#9ca3af', fontWeight: 500 }}>{n}</div>
                        ))}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
                        {cases}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
          </div>

          {/* Pied de panneau sur toute la largeur, sous les deux colonnes. La légende n'y
              apparaît que quand deux plages sont peintes : sans comparaison, nommer la seule
              couleur présente n'apprend rien. */}
          <div style={{
            display: 'flex', justifyContent: brouillonComparer ? 'space-between' : 'flex-end',
            alignItems: 'center', gap: '12px',
            marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb'
          }}>
            {brouillonComparer && (
              <div style={{ display: 'flex', gap: '14px', fontSize: '11px', color: '#6b7280' }}>
                {[
                  { couleur: '#667eea', texte: 'Période' },
                  { couleur: '#f97316', texte: 'Comparaison' }
                ].map(item => (
                  <span key={item.texte} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: item.couleur }} />
                    {item.texte}
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setOuvert(false)}
              style={{
                padding: '6px 16px', border: 'none', background: 'none',
                color: '#6b7280', fontSize: '13px', fontWeight: 500,
                cursor: 'pointer', borderRadius: '6px'
              }}
            >
              Annuler
            </button>
            <button
              onClick={appliquer}
              style={{
                padding: '6px 16px', border: 'none',
                background: aDesChangements ? '#667eea' : 'none',
                color: aDesChangements ? 'white' : '#667eea',
                fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                borderRadius: '6px', transition: 'all 0.2s ease'
              }}
            >
              Appliquer
            </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodSelector;
